import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import GalleryImageType from '~/constants/vdp/galleryImageType';
import { useIsMobile } from '~/context/common/DeviceContext';
import { GalleryImage } from '~/contracts/vdp';
import { useOptimizeImage } from '~/hooks/common';
import { useZoomedImage } from '~/hooks/vdp';
import useGalleryImages from '~/hooks/vdp/useGalleryImages';
import { getIsMobileCapture } from '~/selectors';

export default function useImages() {
  const isMobileCapture = useSelector(getIsMobileCapture);
  const isMobile = useIsMobile();
  const images = useGalleryImages({ includeSafetyFeatures: true });
  const optimizeImage = useOptimizeImage();
  const zoomedHeroUrl = useZoomedImage();
  const shouldUseZoomedImage = isMobile && !isMobileCapture;

  return useMemo(() => {
    const heroAngle = shouldUseZoomedImage ? 1 : 2;
    const heroGalleryData = images.find(
      image => image.positionId === heroAngle
    );
    const nonZoomedHeroUrl = optimizeImage(
      heroGalleryData?.imageUrl || '',
      isMobile ? 440 : 1200
    );
    const heroUrl = shouldUseZoomedImage ? zoomedHeroUrl : nonZoomedHeroUrl;
    const smallImageWidth = isMobile ? 260 : 600;

    const interiorImages = images.filter(feature =>
      Boolean(feature.location === 'interior')
    );
    const preferredInteriorImages = interiorImages.filter(feature =>
      ['Entire Dash', 'Passenger Seat'].includes(feature.title)
    );

    // edc-3 and edc-2 are the first 2 hardcoded exterior images,
    // so we skip them (start at index 2) to prevent potential duplicates

    function getTopImage(): GalleryImage | undefined {
      if (preferredInteriorImages.length > 0) return preferredInteriorImages[0];

      return interiorImages.length > 0 ? interiorImages[0] : images[2];
    }

    function getBottomImage(): GalleryImage | undefined {
      if (preferredInteriorImages.length > 1) return preferredInteriorImages[1];

      return interiorImages.length > 1 ? interiorImages[1] : images[3];
    }

    const topImage = getTopImage();
    const topUrl = optimizeImage(topImage?.imageUrl ?? '', smallImageWidth);

    const bottomImage = getBottomImage();
    const bottomUrl = optimizeImage(
      bottomImage?.imageUrl ?? '',
      smallImageWidth
    );

    return {
      bottomImage: {
        id: bottomImage?.id || '',
        type:
          interiorImages.length > 1
            ? GalleryImageType.Feature
            : GalleryImageType.Exterior,
        url: bottomUrl
      },
      hero: {
        id: heroGalleryData?.id || '',
        type: GalleryImageType.Exterior,
        url: heroUrl
      },
      initialImageId: images[0]?.id,
      remainingImagesLength: Math.max(images.length - 3, 0),
      topImage: {
        id: topImage?.id || '',
        type:
          interiorImages.length > 0
            ? GalleryImageType.Feature
            : GalleryImageType.Exterior,
        url: topUrl
      }
    };
  }, [images, isMobile, optimizeImage, shouldUseZoomedImage, zoomedHeroUrl]);
}
